import { VStack, Stack, HStack, Heading, FormControl, FormLabel, Input, InputGroup, InputRightElement, Text, Link, Checkbox, Center, Spinner, useToast } from '@chakra-ui/react';
import { useNavigate, Link as RouterLink, useParams } from 'react-router-dom';
import { useState, useContext, useCallback, useEffect, type ReactNode } from 'react';
import { Role } from '../hooks/useValidateSession';
import { useSubmitAccountCreation } from '../hooks/useSubmitAccountCreation';
import SessionContext from '../contexts/SessionContext';
import AuthLayout from '../components/auth/AuthLayout';
import { Button } from '../ui';
import PrivacyPolicy from '../modals/PrivacyPolicy';
import { useGetInvite } from '../hooks/useGetInvite';

const formatPhoneNumber = (value: string): string => {
    const stripped = value.replace(/^\+1\s*/, '');
    const digits = stripped.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `+1 (${digits}`;
    if (digits.length <= 6) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const inputStyles = {
    bg: 'gray.50',
    borderColor: 'gray.200',
    borderRadius: 'lg',
    h: '44px',
    _hover: { borderColor: 'gray.300' },
    _focus: { borderColor: 'midnight', bg: 'white', boxShadow: 'none' },
} as const;

const readOnlyInputStyles = {
    ...inputStyles,
    bg: 'gray.100',
    color: 'gray.600',
    cursor: 'not-allowed',
    _hover: { borderColor: 'gray.200' },
    _focus: { ...inputStyles._focus, bg: 'gray.100' },
} as const;

const labelStyles = { fontSize: 'sm', color: 'gray.700', mb: 1.5 } as const;

const FieldRow = ({ children }: { children: ReactNode }) => (
    <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
        {children}
    </Stack>
);

const PasswordInput = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
    const [isVisible, setIsVisible] = useState(false);
    return (
        <InputGroup>
            <Input
                type={isVisible ? 'text' : 'password'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                {...inputStyles}
                paddingEnd="56px"
            />
            <InputRightElement h="44px" w="56px">
                <Text
                    as="button"
                    type="button"
                    fontSize="sm"
                    color="gray.500"
                    fontWeight="medium"
                    onClick={() => setIsVisible(!isVisible)}
                >
                    {isVisible ? 'Hide' : 'Show'}
                </Text>
            </InputRightElement>
        </InputGroup>
    );
};

const InviteAccount = () => {
    const { inviteId } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const sessionContext = useContext(SessionContext);

    const { data: invite, isLoading, error } = useGetInvite(inviteId);
    const submitAccountCreation = useSubmitAccountCreation();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

    // The invited email is fixed, so seed the username from it as soon as it loads.
    useEffect(() => {
        if (invite?.email && !username.trim()) {
            setUsername(invite.email.split('@')[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invite?.email]);

    // Employee invites carry the name the team manager entered.
    useEffect(() => {
        if (invite?.firstName && !firstName.trim()) setFirstName(invite.firstName);
        if (invite?.lastName && !lastName.trim()) setLastName(invite.lastName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invite?.firstName, invite?.lastName]);

    const getPositionDisplay = useCallback((position: Role) => {
        switch (position) {
            case 'central-parts': return 'Central Parts';
            case 'parts-manager': return 'Parts Manager';
            case 'executive': return 'Executive';
            case 'estimator': return 'Estimator';
            case 'gm': return 'GM';
            case 'it': return 'IT';
            case 'invoicing': return 'Invoicing';
            case 'technician': return 'Technician';
            case 'other': return 'Other';
            default: return position;
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast({ title: 'Error', description: 'Passwords do not match', status: 'error', duration: 3000, isClosable: true });
            return;
        }

        if (!username.trim()) {
            toast({ title: 'Missing Information', description: 'Please enter a username', status: 'warning', duration: 3000, isClosable: true });
            return;
        }

        if (!invite) return;

        submitAccountCreation.mutateAsync({
            email: invite.email,
            username: username.trim() || invite.email.split('@')[0],
            password,
            lastName,
            firstName,
            locationId: invite.locationId,
            position: invite.position,
            organizationId: invite.organizationId,
            inviteId,
        })
            .then(res => {
                if (res.data.sessionId) {
                    toast({ title: 'Success', description: 'Account created successfully!', status: 'success', duration: 3000, isClosable: true });
                    sessionContext?.login(res.data.sessionId as string);
                    navigate(res.data.landingPath ?? '/dashboard');
                } else {
                    toast({ title: res.data.error || res.data.message || 'Could not create account. Please try again.', status: 'error', duration: 5000, isClosable: true });
                }
            })
            .catch((error) => {
                const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
                toast({ title: serverMessage || 'An unexpected error occurred.', status: 'error', duration: 5000, isClosable: true });
            });
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <Center py={12}>
                    <Spinner size="lg" color="brand.solid" />
                </Center>
            );
        }

        if (error || !invite) {
            return (
                <VStack spacing={6} align="stretch">
                    <VStack spacing={1} align="stretch">
                        <Heading fontFamily="heading" fontSize="2xl" fontWeight="bold" color="gray.900">
                            This invite link isn't valid
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                            It may have been used already or revoked. Log in if you've already created your account, or ask whoever invited you for a new link.
                        </Text>
                    </VStack>
                    <Button w="full" h="44px" onClick={() => navigate('/login')}>
                        Go to log in
                    </Button>
                </VStack>
            );
        }

        return (
            <VStack spacing={6} as="form" onSubmit={handleSubmit} noValidate align="stretch">
                <VStack spacing={1} align="stretch">
                    <Heading fontFamily="heading" fontSize="2xl" fontWeight="bold" color="gray.900">
                        Welcome to Yaxle
                    </Heading>
                    <Text fontSize="sm" color="gray.500">
                        {invite.internal ? 'Create your account to join the Yaxle team.' : 'Create your account to join your team.'}
                    </Text>
                </VStack>

                <VStack spacing={4} align="stretch">
                    <FieldRow>
                        <FormControl isRequired>
                            <FormLabel {...labelStyles}>First name</FormLabel>
                            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} {...inputStyles} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel {...labelStyles}>Last name</FormLabel>
                            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} {...inputStyles} />
                        </FormControl>
                    </FieldRow>

                    <FieldRow>
                        <FormControl>
                            <FormLabel {...labelStyles}>Position</FormLabel>
                            <Input
                                value={invite.internal ? 'Yaxle employee' : getPositionDisplay(invite.position)}
                                isReadOnly
                                {...readOnlyInputStyles}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel {...labelStyles}>Location</FormLabel>
                            <Input
                                value={invite.internal ? 'Every organization' : invite.locationName}
                                isReadOnly
                                {...readOnlyInputStyles}
                            />
                        </FormControl>
                    </FieldRow>

                    <FieldRow>
                        <FormControl>
                            <FormLabel {...labelStyles}>Phone number</FormLabel>
                            <Input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                                placeholder="+1 (555) 123-4567"
                                {...inputStyles}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel {...labelStyles}>Email</FormLabel>
                            <Input type="email" value={invite.email} isReadOnly {...readOnlyInputStyles} />
                        </FormControl>
                    </FieldRow>

                    <FormControl isRequired>
                        <FormLabel {...labelStyles}>Username</FormLabel>
                        <Input
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Choose a username"
                            {...inputStyles}
                        />
                    </FormControl>

                    <FieldRow>
                        <FormControl isRequired>
                            <FormLabel {...labelStyles}>Password</FormLabel>
                            <PasswordInput value={password} onChange={setPassword} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel {...labelStyles}>Confirm password</FormLabel>
                            <PasswordInput value={confirmPassword} onChange={setConfirmPassword} />
                        </FormControl>
                    </FieldRow>
                </VStack>

                <Checkbox
                    isChecked={privacyAgreed}
                    onChange={(e) => setPrivacyAgreed(e.target.checked)}
                    colorScheme="brand"
                >
                    {/* The checkbox label wraps this link; preventDefault stops a click from toggling it. */}
                    <Text fontSize="sm" color="gray.600">
                        I agree to the{' '}
                        <Link
                            color="brand.fg"
                            fontWeight="medium"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={(e) => { e.preventDefault(); setIsPrivacyModalOpen(true); }}
                        >
                            Privacy Policy
                        </Link>
                    </Text>
                </Checkbox>

                <VStack spacing={5} align="stretch">
                    <Button
                        type="submit"
                        w="full"
                        h="44px"
                        isLoading={submitAccountCreation.isPending}
                        loadingText="Creating account..."
                        isDisabled={!privacyAgreed}
                    >
                        Create account
                    </Button>
                    <Text textAlign="center" fontSize="sm" color="gray.500">
                        Already have an account?{' '}
                        <Text as={RouterLink} to="/login" color="brand.fg" fontWeight="medium" _hover={{ textDecoration: 'underline' }}>
                            Log in
                        </Text>
                    </Text>
                </VStack>
            </VStack>
        );
    };

    return (
        <>
            <AuthLayout
                footer={
                    <HStack spacing={4} color="gray.400" fontSize="xs">
                        <Link onClick={() => setIsPrivacyModalOpen(true)} _hover={{ textDecoration: 'underline' }} cursor="pointer">
                            Privacy Policy
                        </Link>
                        <Link href="mailto:contact@yaxle.com" _hover={{ textDecoration: 'underline' }}>
                            contact@yaxle.com
                        </Link>
                    </HStack>
                }
            >
                {renderContent()}
            </AuthLayout>
            <PrivacyPolicy isOpen={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} />
        </>
    );
};

export default InviteAccount;
